import { HttpClient, HttpEvent, HttpHeaders, HttpResponse } from "@angular/common/http";
import { map, Observable, tap } from "rxjs";
import { AuthService } from "./auth.service";

export abstract class AbstractHttpService {
	constructor(private httpClient: HttpClient, private context: string, private authService: AuthService | undefined) { }

	protected doGet<T>(extendAuth: boolean, path: string): Observable<T> {
		return this.httpClient.get<T>(this.getAdditionalParameters(`${this.context}${path}`), {
			headers: this.getHeaders(extendAuth),
			observe: 'response'
		}).pipe(
			tap({ next: (response: HttpResponse<T>) => this.updateAuth(response.headers) }),
			map((response: HttpResponse<T>) => response.body!)
		);
	}
	protected doPost<T>(extendAuth: boolean, path: string, payload: any): Observable<T> {
		return this.httpClient.post<T>(this.getAdditionalParameters(`${this.context}${path}`), payload, {
			headers: this.getHeaders(extendAuth),
			observe: 'response'
		}).pipe(
			tap({ next: (response: HttpResponse<T>) => this.updateAuth(response.headers) }),
			map((response: HttpResponse<T>) => response.body!)
		);
	}
	protected doPostWithAuth<T>(path: string, jwtToken: string, payload: any): Observable<T> {
		let headers = new HttpHeaders();
		headers = headers.set('Authorization', `Bearer ${jwtToken}`);

		return this.httpClient.post<T>(this.getAdditionalParameters(`${this.context}${path}`), payload, {
			headers: headers,
			observe: 'response'
		}).pipe(
			tap({ next: (response: HttpResponse<T>) => this.updateAuth(response.headers) }),
			map((response: HttpResponse<T>) => response.body!)
		);
	}
	protected doUploadFile<T>(extendAuth: boolean, path: string, payload: any): Observable<HttpEvent<T>> {
		const formData = new FormData();
		formData.append('file', payload);

		return this.httpClient.post<T>(this.getAdditionalParameters(`${this.context}${path}`), formData, {
			headers: this.getHeaders(extendAuth),
			reportProgress: true,
			observe: 'events'
		});
	}
	protected doUploadFileWithAuth<T>(path: string, jwtToken: string, payload: any): Observable<HttpEvent<T>> {
		let headers = new HttpHeaders();
		headers = headers.set('Authorization', `Bearer ${jwtToken}`);

		const formData = new FormData();
		formData.append('file', payload);

		return this.httpClient.post<T>(this.getAdditionalParameters(`${this.context}${path}`), formData, {
			headers: headers,
			reportProgress: true,
			observe: 'events'
		});
	}
	protected doDownloadFile(extendAuth: boolean, path: string): Observable<HttpResponse<Blob>> {
		return this.httpClient.get(this.getAdditionalParameters(`${this.context}${path}`), {
			headers: this.getHeaders(extendAuth),
			responseType: 'blob',
			observe: 'response'
		}).pipe(tap(response => {
			const contentDisposition = response.headers.get('Content-Disposition');
			const filenameMatch = contentDisposition?.match(/filename="(.+)"/);
			const filename = filenameMatch ? filenameMatch[1] : 'download.csv';

			const blob = new Blob([response.body!], { type: response.headers.get('Content-Type') || 'application/octet-stream' });

			const link = document.createElement('a');
			link.href = window.URL.createObjectURL(blob);
			link.download = filename;
			link.click();

			window.URL.revokeObjectURL(link.href);
		}));
	}
	protected doPostAndDownloadFile(extendAuth: boolean, path: string, payload: any): Observable<HttpResponse<Blob>> {
		return this.httpClient.post(this.getAdditionalParameters(`${this.context}${path}`), payload, {
			headers: this.getHeaders(extendAuth),
			responseType: 'blob',
			observe: 'response'
		}).pipe(tap(response => {
			const contentDisposition = response.headers.get('Content-Disposition');
			const filenameMatch = contentDisposition?.match(/filename="(.+)"/);
			const filename = filenameMatch ? filenameMatch[1] : 'download.csv';

			const blob = new Blob([response.body!], { type: response.headers.get('Content-Type') || 'application/octet-stream' });

			const link = document.createElement('a');
			link.href = window.URL.createObjectURL(blob);
			link.download = filename;
			link.click();

			window.URL.revokeObjectURL(link.href);
		}));
	}
	protected doPostForFile(extendAuth: boolean, path: string, payload: any): Observable<HttpResponse<Blob>> {
		return this.httpClient.post(this.getAdditionalParameters(`${this.context}${path}`), payload, {
			headers: this.getHeaders(extendAuth),
			responseType: 'blob',
			observe: 'response'
		});
	}
	protected doGetForFile(extendAuth: boolean, path: string): Observable<HttpResponse<Blob>> {
		return this.httpClient.get(this.getAdditionalParameters(`${this.context}${path}`), {
			headers: this.getHeaders(extendAuth),
			responseType: 'blob',
			observe: 'response'
		});
	}
	protected doGetText(extendAuth: boolean, path: string): Observable<string> {
		return this.httpClient.get(`${this.context}${path}`, {
			headers: this.getHeaders(extendAuth),
			responseType: 'text',
			observe: 'response'
		}).pipe(
			tap({ next: (response: HttpResponse<string>) => this.updateAuth(response.headers) }),
			map((response: HttpResponse<string>) => response.body!)
		);
	}

	private getAdditionalParameters(url: string): string {
		if (this.authService && this.authService.hierarchy) {
			if (url.indexOf("?") == -1) {
				url = url + "?";
			} else {
				url = url + "&";
			}
			url = url + "hierarchyId=" + this.authService.hierarchy.id;
		}
		return url;
	}
	private getHeaders(extendAuth: boolean) {
		if (this.authService == undefined) {
			return new HttpHeaders();
		}
		const token = this.authService.getJwtToken();
		if (token == null) {
			return new HttpHeaders();
		}
		let headers = new HttpHeaders();
		headers = headers.set('Authorization', `Bearer ${token}`);
		if (this.authService.hierarchy) {
			headers = headers.set('Hierarchy', `${this.authService.hierarchy.id}`);
		}
		if (extendAuth == true) {
			headers = headers.set('Update-JWT', 'true');
		}
		return headers;
	}
	private updateAuth(response: HttpHeaders) {
		if (this.authService == undefined) {
			return;
		}
		const expiresIn = response.get('Expires-In');
		const jwtToken = response.get('Jwt-Token');

		if (expiresIn && jwtToken) {
			this.authService.updateAuthenticate(true, jwtToken, +expiresIn);
		}
	}


	protected toApiDate(value: Date): string {
		if (typeof value === 'string') return value; // already "yyyy-MM-dd" from input
		// Use local date parts to avoid UTC midnight shifting the day
		const d = value instanceof Date ? value : new Date(value);
		const yyyy = d.getFullYear();
		const mm = String(d.getMonth() + 1).padStart(2, '0');
		const dd = String(d.getDate()).padStart(2, '0');
		return `${yyyy}-${mm}-${dd}`;
	}
}
